import { useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { Wand2, Plus, DollarSign, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

/**
 * Editable suggestions: tick the ones you want, reword or resize them,
 * and add them all at once. Used by the setup guide and by the rewards
 * pages' empty states; `returnTo` ("milestones" / "student_milestones")
 * brings the admin back to the page they started on.
 *
 * suggestions: [{ kind, amount, prize }] — amount in dollars or minutes
 * secondaryAction: optional node beside the add button ("Add your own instead")
 * onAdded: called once they're saved (pages stay mounted across visits,
 *          so the parent closes the box rather than relying on a remount)
 *
 * Looks deliberately unlike saved rewards — a "Suggested" label, dashed
 * draft rows — so nobody mistakes it for rewards they already have.
 */
export default function SuggestedRewards({ title, description, suggestions, appliesTo, path, noun, returnTo, secondaryAction, onAdded }) {
  const { errors } = usePage().props;
  const [rows, setRows] = useState(() =>
    suggestions.map((s, i) => ({ id: i, checked: true, kind: s.kind, prize: s.prize, amount: String(s.amount) }))
  );
  const [processing, setProcessing] = useState(false);
  const chosen = rows.filter((r) => r.checked && r.prize.trim() && Number(r.amount) > 0);
  const update = (id, changes) => setRows((current) => current.map((r) => (r.id === id ? { ...r, ...changes } : r)));

  const submit = () => {
    router.post(
      path,
      {
        applies_to: appliesTo,
        return_to: returnTo,
        rewards: chosen.map((r) => ({
          kind: r.kind,
          prize: r.prize.trim(),
          threshold_amount: r.kind === "money" ? Math.round(Number(r.amount) * 100) : Math.round(Number(r.amount)),
        })),
      },
      { preserveScroll: true, onStart: () => setProcessing(true), onFinish: () => setProcessing(false), onSuccess: () => onAdded?.() }
    );
  };

  return (
    <section aria-label={title} className="rounded-xl border border-primary/25 bg-primary/[0.04] p-5 text-left">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Wand2 className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{title}</span>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary">Suggested</span>
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {/* Dashed, draft-looking rows: these aren't rewards yet. */}
      <ul className="mt-4 space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className={cn(
              "flex flex-col gap-2 rounded-lg border border-dashed p-3 transition-opacity sm:flex-row sm:items-center",
              row.checked ? "border-primary/35 bg-card" : "border-border bg-card/50 opacity-55"
            )}
          >
            <label className="flex flex-1 items-center gap-3">
              <Checkbox
                checked={row.checked}
                onChange={(e) => update(row.id, { checked: e.target.checked })}
                aria-label={`Include ${row.prize}`}
              />
              <input
                type="text"
                value={row.prize}
                onChange={(e) => update(row.id, { prize: e.target.value })}
                aria-label={`Prize ${row.id + 1}`}
                className={cn(
                  "h-9 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-sm font-medium text-foreground hover:border-input focus-visible:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  !row.checked && "line-through decoration-muted-foreground/50"
                )}
              />
            </label>
            <div className="flex items-center gap-1.5 pl-8 text-sm text-muted-foreground sm:pl-0">
              {row.kind === "money" ? <DollarSign className="h-3.5 w-3.5" aria-hidden="true" /> : <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />}
              <span>{row.kind === "money" ? "at $" : "at"}</span>
              <input
                type="number"
                min="1"
                inputMode="numeric"
                value={row.amount}
                onChange={(e) => update(row.id, { amount: e.target.value })}
                aria-label={row.kind === "money" ? "Dollars raised" : "Minutes read"}
                className="h-9 w-24 rounded-md border border-input bg-card px-2 text-right text-sm text-foreground font-tabular focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {row.kind === "minutes" && <span>{appliesTo === "student" ? "minutes each" : "minutes"}</span>}
            </div>
          </li>
        ))}
      </ul>

      {errors?.rewards && <p className="mt-3 text-sm text-destructive">{errors.rewards}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={submit} disabled={processing || chosen.length === 0}>
          <Plus className="h-4 w-4" />
          {processing ? "Adding…" : chosen.length === rows.length ? `Add ${chosen.length === 1 ? "this" : "these"} ${chosen.length} ${noun}${chosen.length === 1 ? "" : "s"}` : `Add ${chosen.length} ${noun}${chosen.length === 1 ? "" : "s"}`}
        </Button>
        {secondaryAction}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Untick any you don't want and change the wording or amounts. Nothing is added until you choose.</p>
    </section>
  );
}
